import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "../components/SEO";
import {
  MapPin, Search, Loader2, Navigation, Send, ArrowLeft, Home,
  Building2, CheckCircle2, Filter, X,
} from "lucide-react";
import { PROPERTY_TYPES, TYPE_BY_VALUE } from "../lib/propertyTypes";
import NearMeSearch from "../components/NearMeSearch";
import { formatUSPhone } from "../lib/phone";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const publicClient = axios.create({ baseURL: `${API_BASE}/api` });
const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

export default function FindSpace() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    message: "",
    address: "",
    radius_miles: 25,
    property_types: [],
    min_units: "",
  });
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [result, setResult] = useState(null);

  const togglePropertyType = (val) => {
    setForm((f) => ({
      ...f,
      property_types: f.property_types.includes(val)
        ? f.property_types.filter((t) => t !== val)
        : [...f.property_types, val],
    }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser does not support geolocation.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setForm((f) => ({ ...f, address: `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}` }));
        toast.success("Location locked in.");
      },
      (err) => { setLocating(false); toast.error(err.message || "Could not get location."); },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) { toast.error("Tell us where you're looking."); return; }
    if (!form.message.trim()) { toast.error("Describe what kind of space you need."); return; }
    setBusy(true);
    try {
      const { data } = await publicClient.post("/public/space-requests", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        address: form.address,
        radius_miles: Number(form.radius_miles),
        property_types: form.property_types,
        min_units: form.min_units ? Number(form.min_units) : null,
      });
      setResult(data);
      toast.success(`Broadcast sent to ${data.matching_owners} investor${data.matching_owners === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-marketing text-white">
      <SEO
        title="Find Commercial Space — Broadcast to Every Investor at Once"
        description="Looking for office, retail, warehouse, or mixed-use space? One request reaches every investor with a matching property. Free for tenants — no broker needed."
        path="/find-space"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Find Commercial Space",
          url: "https://realtyvaultmgr.com/find-space",
          description: "Broadcast a commercial real-estate space request to every matching investor at once.",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://realtyvaultmgr.com/find-space?address={address}",
            "query-input": "required name=address",
          },
        }}
      />
      <header className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="find-space-home">
          <div className="w-8 h-8 rounded-md bg-white text-slate-900 flex items-center justify-center font-bold text-sm">R</div>
          <span className="font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>RealtyVault <span className="text-white/50">Manager</span></span>
        </Link>
        <Link to="/signup" className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-full bg-white text-slate-900 hover:bg-slate-100 transition-colors hidden sm:inline-block">
          List your property →
        </Link>
      </header>

      <section className="px-6 md:px-12 lg:px-24 pt-4 pb-8 max-w-5xl mx-auto">
        <Link to="/" className="text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1 w-fit mb-6" data-testid="find-space-back">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <div className="max-w-2xl">
          <div className="overline text-white/40 mb-3">For tenants</div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Looking for space? <span className="text-white/40 italic font-light">Tell the whole market at once.</span>
          </h1>
          <p className="mt-4 text-white/60 text-base md:text-lg">
            One broadcast reaches every investor with a property matching your criteria. No more emailing brokers one by one.
          </p>
        </div>
      </section>

      {/* BROADCAST FORM */}
      <section className="px-6 md:px-12 lg:px-24 pb-8 max-w-5xl mx-auto">
        {result ? (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-300/30 p-8 backdrop-blur-md" data-testid="find-space-result">
            <CheckCircle2 className="w-10 h-10 text-emerald-300 mb-3" />
            <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Broadcast sent.</h2>
            <p className="mt-3 text-white/70">
              We notified <b className="text-white">{result.matching_owners}</b> investor{result.matching_owners === 1 ? "" : "s"} with{" "}
              <b className="text-white">{result.matching_properties}</b> matching propert{result.matching_properties === 1 ? "y" : "ies"} in your area.
              Expect responses at <b className="text-white">{form.email}</b> over the next 24–48 hours.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => { setResult(null); setForm({ ...form, message: "" }); }}
                data-testid="find-space-another"
                className="px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-bold hover:bg-slate-100 transition-colors"
              >
                Post another request
              </button>
              <Link
                to="/#near-me"
                className="px-5 py-2.5 rounded-full border border-white/20 text-white text-sm font-mono uppercase tracking-widest hover:bg-white/10 transition-colors"
              >
                Browse properties near you →
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-6 md:p-8 space-y-6" data-testid="find-space-form">
            {/* Location */}
            <div>
              <Label>Where?</Label>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 mt-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    required
                    placeholder="ZIP, city, or full address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="find-space-address"
                    className="w-full pl-9 pr-3 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm font-mono focus:outline-none focus:border-white/60"
                  />
                </div>
                <select
                  value={form.radius_miles}
                  onChange={(e) => setForm({ ...form, radius_miles: Number(e.target.value) })}
                  data-testid="find-space-radius"
                  className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white text-sm font-mono focus:outline-none"
                >
                  {RADIUS_OPTIONS.map((r) => <option key={r} value={r} className="bg-white text-slate-900">{r} mi</option>)}
                </select>
                <button
                  type="button"
                  onClick={useMyLocation}
                  disabled={locating}
                  data-testid="find-space-geolocate"
                  className="px-4 py-3 rounded-full border border-white/20 text-white text-sm font-mono flex items-center justify-center gap-2 hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                  <span className="hidden md:inline">Use my location</span>
                </button>
              </div>
            </div>

            {/* Property types */}
            <div>
              <Label sub="(optional — leave empty for any)"><Filter className="w-3 h-3 mr-1 inline" />What type of space?</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PROPERTY_TYPES.map((pt) => {
                  const on = form.property_types.includes(pt.value);
                  return (
                    <button
                      type="button"
                      key={pt.value}
                      onClick={() => togglePropertyType(pt.value)}
                      data-testid={`find-space-type-${pt.value}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${
                        on
                          ? "bg-white text-slate-900 border-white"
                          : "bg-white/5 text-white/70 border-white/20 hover:bg-white/10"
                      }`}
                    >
                      {pt.emoji} {pt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Min units */}
            <div>
              <Label sub="(optional)">Minimum units</Label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 4"
                value={form.min_units}
                onChange={(e) => setForm({ ...form, min_units: e.target.value })}
                data-testid="find-space-min-units"
                className="mt-2 w-40 px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm font-mono focus:outline-none focus:border-white/60"
              />
            </div>

            {/* Message */}
            <div>
              <Label>What exactly are you looking for?</Label>
              <textarea
                required
                rows={4}
                placeholder="e.g. 3,000 sqft retail corner, 3-year lease, ground-floor, parking required. Budget ~$4k/mo."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="find-space-message"
                className="mt-2 w-full px-3 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm font-mono focus:outline-none focus:border-white/60"
              />
            </div>

            {/* Contact */}
            <div className="pt-4 border-t border-white/10">
              <Label sub="So investors can reach you">Contact info</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                <input
                  required
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="find-space-name"
                  className="px-3 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm font-mono focus:outline-none focus:border-white/60"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="find-space-email"
                  className="px-3 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm font-mono focus:outline-none focus:border-white/60"
                />
                <input
                  placeholder="Phone (optional)"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={formatUSPhone(form.phone)}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                  data-testid="find-space-phone"
                  className="px-3 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm font-mono focus:outline-none focus:border-white/60"
                />
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/40">
                We never share your info with third parties.
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="find-space-submit"
                className="px-6 py-3 rounded-full bg-white text-slate-900 text-sm font-bold flex items-center gap-2 hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Broadcast my request
              </button>
            </div>
          </form>
        )}
      </section>

      {/* ALSO BROWSE NEARBY */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 max-w-5xl mx-auto">
        <div className="max-w-2xl mb-8 pt-8 border-t border-white/10">
          <div className="overline text-white/40">Or browse existing listings</div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight mt-2" style={{ fontFamily: "Outfit" }}>
            See what's already on the market.
          </h3>
        </div>
        <NearMeSearch theme="dark" />
      </section>
    </div>
  );
}

function Label({ children, sub }) {
  return (
    <div className="overline text-white/60 flex items-baseline gap-2">
      <span>{children}</span>
      {sub && <span className="text-[10px] normal-case text-white/30 font-mono tracking-normal">{sub}</span>}
    </div>
  );
}
